import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ShieldCheck, Loader2, ExternalLink, CheckCircle2, AlertCircle, XCircle, MinusCircle, Wand2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';

const STATUS_META = {
  yes:            { color: 'text-emerald-700 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-950/40', icon: CheckCircle2, label: 'OK' },
  partial:        { color: 'text-amber-700 dark:text-amber-400',     bg: 'bg-amber-50 dark:bg-amber-950/40',     icon: AlertCircle,  label: 'Частично' },
  no:             { color: 'text-rose-700 dark:text-rose-400',       bg: 'bg-rose-50 dark:bg-rose-950/40',       icon: XCircle,      label: 'Липсва' },
  not_applicable: { color: 'text-slate-500 dark:text-slate-400',     bg: 'bg-slate-50 dark:bg-slate-900/40',     icon: MinusCircle,  label: 'N/A' },
};

export default function ComplianceCheckDialog({ open, onOpenChange, userId, chapterId, chapterTitle, onChapterUpdated }) {
  const [guidelines, setGuidelines] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [loadingGuidelines, setLoadingGuidelines] = useState(false);

  // Auto-fix state: keyed by itemId
  // { [itemId]: { loading, suggestion, applied, error } }
  const [fixes, setFixes] = useState({});

  useEffect(() => {
    if (!open) return;
    setLoadingGuidelines(true);
    thesisApi.listGuidelines()
      .then(d => setGuidelines(d.guidelines || []))
      .catch(() => toast.error('Неуспешно зареждане на EQUATOR ръководствата'))
      .finally(() => setLoadingGuidelines(false));
  }, [open]);

  const runCheck = async () => {
    if (!selectedId) { toast.error('Избери ръководство'); return; }
    setRunning(true);
    setResult(null);
    setFixes({});
    try {
      const data = await thesisApi.checkCompliance(userId, chapterId, selectedId, 'bg');
      setResult(data);
      toast.success(`Compliance score: ${data.overall_score}/100`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Compliance check failed');
    } finally {
      setRunning(false);
    }
  };

  const runAutoFix = async (itemId, apply = false) => {
    setFixes(prev => ({ ...prev, [itemId]: { ...(prev[itemId] || {}), loading: true, error: null } }));
    try {
      const data = await thesisApi.fixComplianceItem(userId, chapterId, {
        guideline_id: selectedId,
        item_id: itemId,
        apply,
        language: 'bg',
      });
      setFixes(prev => ({ ...prev, [itemId]: { loading: false, suggestion: data, applied: !!data.applied, error: null } }));
      if (apply) {
        toast.success('Параграфът беше добавен към главата');
        if (onChapterUpdated) onChapterUpdated();
      } else {
        toast.success('Готово предложение');
      }
    } catch (e) {
      setFixes(prev => ({ ...prev, [itemId]: { loading: false, error: e.response?.data?.detail || 'Auto-fix failed' } }));
      toast.error(e.response?.data?.detail || 'Auto-fix failed');
    }
  };

  const dismissFix = (itemId) => {
    setFixes(prev => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
  };

  const grouped = (result?.items || []).reduce((acc, it) => {
    const s = it.section || 'Other';
    if (!acc[s]) acc[s] = [];
    acc[s].push(it);
    return acc;
  }, {});

  const selectedGuideline = guidelines.find(g => g.id === selectedId);
  const missingCount = (result?.items || []).filter(it => it.status === 'no' || it.status === 'partial').length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="compliance-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif">
            <ShieldCheck className="w-5 h-5 text-teal-700" />
            EQUATOR Compliance Check
          </DialogTitle>
          <DialogDescription>
            Глава: <span className="font-medium text-foreground">{chapterTitle}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Picker */}
          <div className="flex gap-3 items-end flex-wrap">
            <div className="flex-1 min-w-[280px]">
              <label className="text-sm font-medium block mb-1.5">Reporting guideline</label>
              <Select value={selectedId} onValueChange={setSelectedId} disabled={loadingGuidelines}>
                <SelectTrigger data-testid="compliance-guideline-select">
                  <SelectValue placeholder={loadingGuidelines ? 'Зареждам…' : 'Избери ръководство (CONSORT, STROBE, PRISMA, …)'} />
                </SelectTrigger>
                <SelectContent>
                  {guidelines.map(g => (
                    <SelectItem key={g.id} value={g.id}>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{g.name}</span>
                        <span className="text-xs text-muted-foreground">— {g.item_count} items</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedGuideline && (
                <p className="text-xs text-muted-foreground mt-1.5">
                  {selectedGuideline.summary}{' '}
                  <a href={selectedGuideline.official_url} target="_blank" rel="noreferrer" className="text-teal-700 inline-flex items-center gap-0.5 hover:underline">
                    официален източник <ExternalLink className="w-3 h-3" />
                  </a>
                </p>
              )}
            </div>
            <Button onClick={runCheck} disabled={!selectedId || running} data-testid="compliance-run-btn" className="bg-teal-700 hover:bg-teal-800">
              {running ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Анализ…</> : 'Пусни проверка'}
            </Button>
          </div>

          {running && (
            <div className="text-center py-8 text-muted-foreground">
              <Loader2 className="w-6 h-6 mx-auto mb-3 animate-spin" />
              GPT-5.2 преглежда главата срещу {selectedGuideline?.name}…
            </div>
          )}

          {/* Result */}
          {result && !running && (
            <Card className="border-teal-200/60 dark:border-teal-900/60" data-testid="compliance-result">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <CardTitle className="font-serif text-xl">
                      {result.guideline_name} — Compliance Score
                    </CardTitle>
                    {missingCount > 0 && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {missingCount} items за подобрение · кликни <Wand2 className="w-3 h-3 inline mx-0.5 text-teal-700" />
                        Поправи за AI предложение
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-teal-700 dark:text-teal-400">
                      {result.overall_score}<span className="text-base text-muted-foreground">/100</span>
                    </div>
                  </div>
                </div>
                <Progress value={result.overall_score} className="h-2 mt-2" />
                {result.summary && (
                  <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{result.summary}</p>
                )}
              </CardHeader>
              <CardContent className="space-y-5">
                {Object.entries(grouped).map(([section, items]) => (
                  <div key={section}>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 font-semibold">{section}</div>
                    <div className="space-y-2">
                      {items.map((it, idx) => {
                        const meta = STATUS_META[it.status] || STATUS_META.no;
                        const Icon = meta.icon;
                        const canFix = it.status === 'no' || it.status === 'partial';
                        const fixState = fixes[it.id];
                        return (
                          <div key={`${it.id}-${idx}`} className={`rounded-lg p-3 ${meta.bg} border border-border/40`}>
                            <div className="flex items-start gap-3">
                              <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${meta.color}`} />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                  <Badge variant="outline" className="font-mono text-[10px]">{it.id}</Badge>
                                  <Badge variant="secondary" className={`text-[10px] ${meta.color} bg-transparent`}>{meta.label}</Badge>
                                  {canFix && !fixState?.suggestion && (
                                    <Button
                                      variant="ghost" size="sm"
                                      className="h-6 px-2 text-[10px] text-teal-700 hover:text-teal-800 hover:bg-teal-100 dark:hover:bg-teal-950/60 ml-auto"
                                      onClick={() => runAutoFix(it.id, false)}
                                      disabled={fixState?.loading}
                                      data-testid={`compliance-autofix-${it.id}`}
                                    >
                                      {fixState?.loading
                                        ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Генерирам…</>
                                        : <><Wand2 className="w-3 h-3 mr-1" />Поправи</>}
                                    </Button>
                                  )}
                                </div>
                                {it.comment && <p className="text-sm text-foreground/80 mb-1">{it.comment}</p>}
                                {it.evidence && (
                                  <blockquote className="text-xs text-muted-foreground italic border-l-2 border-teal-600/60 pl-2 mt-1.5">
                                    "{it.evidence}"
                                  </blockquote>
                                )}

                                {/* Auto-fix suggestion panel */}
                                {fixState?.suggestion && (
                                  <div className="mt-3 bg-white dark:bg-slate-900/60 border border-teal-300/60 dark:border-teal-800/60 rounded-md p-3" data-testid={`compliance-fix-result-${it.id}`}>
                                    <div className="flex items-start justify-between gap-2 mb-2">
                                      <div className="flex items-center gap-1.5 text-xs font-semibold text-teal-800 dark:text-teal-300">
                                        <Wand2 className="w-3.5 h-3.5" />
                                        AI предложение
                                        {fixState.applied && <Badge className="ml-1 bg-emerald-600 text-white text-[10px]">✓ Добавено</Badge>}
                                      </div>
                                      <Button
                                        variant="ghost" size="sm"
                                        className="h-5 w-5 p-0"
                                        onClick={() => dismissFix(it.id)}
                                      >
                                        <X className="w-3.5 h-3.5" />
                                      </Button>
                                    </div>

                                    {fixState.suggestion.rationale && (
                                      <p className="text-[11px] text-muted-foreground italic mb-2">
                                        Защо: {fixState.suggestion.rationale}
                                      </p>
                                    )}

                                    {fixState.suggestion.markdown && (
                                      <div className="text-sm leading-relaxed bg-muted/40 rounded p-2 mb-2 whitespace-pre-wrap font-serif">
                                        {fixState.suggestion.markdown}
                                      </div>
                                    )}

                                    {fixState.suggestion.evidence_used?.length > 0 && (
                                      <details className="mb-2">
                                        <summary className="text-[11px] text-muted-foreground cursor-pointer hover:text-foreground">
                                          📚 {fixState.suggestion.evidence_used.length} източника използвани
                                        </summary>
                                        <ul className="mt-1.5 space-y-1.5">
                                          {fixState.suggestion.evidence_used.map((ev, i) => (
                                            <li key={ev.doi || ev.pmid || ev.source_title || `ev-${i}`} className="text-[11px] text-muted-foreground leading-snug pl-3 border-l-2 border-teal-600/40">
                                              <div className="font-medium text-foreground/80">{ev.source_title || 'Untitled'}</div>
                                              {ev.doi && <span className="text-teal-700 dark:text-teal-400">DOI: {ev.doi} </span>}
                                              {ev.pmid && <span className="text-teal-700 dark:text-teal-400">· PMID: {ev.pmid}</span>}
                                              {typeof ev.similarity === 'number' && <span> · {Math.round(ev.similarity * 100)}% match</span>}
                                            </li>
                                          ))}
                                        </ul>
                                      </details>
                                    )}

                                    {!fixState.applied && fixState.suggestion.markdown && (
                                      <div className="flex gap-2 justify-end">
                                        <Button
                                          variant="outline" size="sm"
                                          className="h-7 text-xs"
                                          onClick={() => dismissFix(it.id)}
                                        >
                                          Отказ
                                        </Button>
                                        <Button
                                          size="sm"
                                          className="h-7 text-xs bg-teal-700 hover:bg-teal-800 text-white"
                                          onClick={() => runAutoFix(it.id, true)}
                                          disabled={fixState.loading}
                                          data-testid={`compliance-apply-fix-${it.id}`}
                                        >
                                          {fixState.loading
                                            ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Добавям…</>
                                            : <><Plus className="w-3 h-3 mr-1" />Добави към главата</>}
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {fixState?.error && !fixState?.suggestion && (
                                  <p className="text-xs text-rose-600 dark:text-rose-400 mt-2">⚠ {fixState.error}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
